import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Button,
    Checkbox,
    Input,
    Toast,
    Radio,
    RADIO_SIZES,
    PrimaryButton,
    Menu,
    MenuListItem,
    IconArrowDropDown,
    IconArrowDropUp,
    TabPanel,
    Tab,
    Tabs,
    TextButton,
    IconOpenInNew,
    Loader,
    Card,
    IconAdd,
    TableContainer,
    TableContent,
    Dialog,
    usePreventTableScrollingRef,
    TextRenderer,
    TOAST_VARIANTS,
    BUTTON_VARIANTS
} from "cdk-radial";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import styled from "styled-components";
import "./AppSettings.scss";
import LearnMore from "../../../../common/LearnMore/LearnMore";
import { ProvisioningConstants } from "../../../../common/ProvisioningConstants";
import AppSubscriptions from "../../../../common/AppSubscriptions";
import { JsonTextArea } from "./JsonTextArea";
import { validateUrl } from "../../../../../utils/utils";
import { environmentURLs } from "../../../../common/environment/CaptureEnvironment";
import gql from "graphql-tag";
import moment from "moment";
import { getApolloClient } from "../../../../../containers/GraphQLClient";

const StyledTextButton = styled(TextButton)`
   background-color: transparent;
   border: 2px solid #6B2574;
`;

const UnderlinedTextButton = styled(TextButton)`
    text-decoration: underline;
    font-weight: 600;
    font-size: 14px;
`;

const StyledToast = styled(Toast)`
    div {
        font-weight: 600;
    }
`;

const StyledDialog = styled(Dialog)`
    width: 963px;
    height: 385px;
    border-radius: 8px;
`;

const StyledSaveDialog = styled(Dialog)`
    width: 610px;
    height: 182px;
    border-radius: 8px;
`;

const API_REQUEST_BODY = {
    solutionInfo: {
        id: "sample-id-for-testing",
        name: "Sample Solution Name",
        planInfo: {
            name: "Test Plan",
            description: "Test Plan Description",
        },
    },
    subscriptionInfo: {
        id: "sample-id-for-testing",
        subscribedBy: "john.doe@test.com",
        subscribedOn: "2019-04-09T13:58:10.385Z",
        subscriberEntity: {
            id: "sample-entity-id",
            name: "Test Entity Name",
            address: {
                region: "IL",
                city: "Hoffman Estates",
                street: "1950 Hassell Road",
                countryCode: "US",
                postalCode: "60169",
            },
            phoneNumber: "1234567899",
            website: "www.testwebsite.test",
        },
    },
    notificationType: "Subscription",
};


const ChangeHistoryCell = ({ value }) => (
    <span className="app-settings-change-history-cell">
        {value || "-"}
    </span>
);

const CHANGE_HISTORY_COLUMN_DEFS = [
    {
        cellRenderer: ChangeHistoryCell,
        field: 'fieldName',
        headerName: 'Field Name',
        tooltipField: 'fieldName',
        width: 185,
        maxWidth: 185,
    },
    {
        cellRenderer: ChangeHistoryCell,
        field: 'oldValue',
        headerName: 'Old Value',
        tooltipField: 'oldValue',
        width: 185,
        maxWidth: 185,
    },
    {
        cellRenderer: ChangeHistoryCell,
        field: 'newValue',
        headerName: 'New Value',
        tooltipField: 'newValue',
        width: 185,
        maxWidth: 185,
    },
    {
        cellRenderer: ChangeHistoryCell,
        field: 'changedBy',
        headerName: 'Changed By',
        tooltipField: 'changedBy',
        width: 185,
        maxWidth: 185,
    },
    {
        cellRenderer: ChangeHistoryCell,
        headerName: 'Date & Time',
        tooltipValueGetter: ({ value }) => value,
        valueGetter: ({ data }) =>
            data?.dateTime ||
            (data?.changedOn
                ? moment(data.changedOn).format(CHANGE_HISTORY_DATE_FORMAT)
                : "-"),
        width: 185,
        maxWidth: 185,
    }
];

const NOTIFY_URL_TEST_MUTATION = gql`
    mutation NotifyUrlTest($command: NotifyUrlCmd) {
        notifyUrl(command: $command) {
            status
        }
    }
`;

const GET_METADATA_SUBSCRIPTIONS_QUERY = gql`
    query($solutionId: ID, $randomId: ID) {
        getMetadataSubscriptions(solutionId: $solutionId, randomId: $randomId) {
            id
            status
            appSubscriptionActivationState
        }
    }
`;

const PENDING_ACTIVATION_STATE = "Pending";

const SUBSCRIPTION_VISIBLE_INPUT_COUNT = 3;
const SUBSCRIPTION_MAX_INPUT_COUNT = 5;
const CHANGE_HISTORY_DATE_FORMAT = "MMM D, YYYY hh:mmA";
const EMAIL_VALIDATION_MESSAGE = "Please enter a valid email address.";
const URL_VALIDATION_MESSAGE = "Please enter a valid URL.";

const APP_ACTIVATION_OPTIONS = [
    {
        id: "radio-app-activation-auto",
        label: "Auto",
        value: AppSubscriptions.AUTOMATIC,
        description:
            "When a user subscribes to the app, they will automatically be able to connect and install the app.",
    },
    {
        id: "radio-app-activation-manual",
        label: "Manual",
        value: AppSubscriptions.MANUAL,
        description:
            "When a user subscribes to the app, the publisher will have to manually confirm the subscription.",
    },
];

const PROVISIONING_OPTIONS = [
    {
        id: "radio-provisioning-manual",
        label: "Manual / Default Provisioning",
        value: ProvisioningConstants.type.MANUAL,
        description: "You will do the provisioning outside Fortellis manually.",
    },
    {
        id: "radio-provisioning-redirect",
        label: "Provisioning via Website URL",
        value: ProvisioningConstants.type.REDIRECT_URL,
        description:
            "To capture additional information from the buyer you may need to create a website. Fortellis will redirect the buyer to specified URL when the buyer starts provisioning.",
    },
];

const REDIRECT_TYPE_OPTIONS = [
    {
        id: "radio-redirect-subscription-id",
        label: "Subscription ID",
        value: "subscriptionID",
    },
    {
        id: "radio-redirect-entity-id",
        label: "Entity ID",
        value: "entityID",
    },
];

const getProvisioningTypeLabel = (value) => {
    if (value === ProvisioningConstants.type.MANUAL) {
        return "Manual";
    }

    if (value === ProvisioningConstants.type.REDIRECT_URL) {
        return "Automatic";
    }

    return value || "-";
};

const getRedirectTypeLabel = (value) => {
    const option = REDIRECT_TYPE_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value || "-";
};

const getLatestSolution = (solutions) => {
    if (!Array.isArray(solutions) || !solutions.length) {
        return null;
    }

    const approvedOrPublishedSolutions = solutions.filter((solution) => {
        const status = (solution?.status || "").toLowerCase();
        return status === "approved" || status === "published";
    });

    const source = approvedOrPublishedSolutions.length
        ? approvedOrPublishedSolutions
        : solutions;

    return [...source].sort(
        (a, b) =>
            parseInt(b.listingVersion || 0, 10) - parseInt(a.listingVersion || 0, 10),
    )[0];
};

const mapEmails = (value) => {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.filter(Boolean);
    }

    return value
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean);
};

const mapEmailsToFixedCount = (emails, count) => {
    const normalizedEmails = mapEmails(emails);
    return Array.from(
        { length: count },
        (_, index) => normalizedEmails[index] || "",
    );
};

const getSolutionSettingsSource = (solution) => {
    if (!solution) {
        return null;
    }

    return solution.solutiondata || solution;
};

const getComparableFormState = (state) => ({
    technicalSupportEmail: (state.technicalSupportEmail || "").trim(),
    subscriptionNotificationEmails: (
        state.subscriptionNotificationEmails || []
    ).map((email) => (email || "").trim()),
    provisioningType: state.provisioningType || ProvisioningConstants.type.MANUAL,
    apiNotificationEnabled: Boolean(state.apiNotificationEnabled),
    endpointUrl: Boolean(state.apiNotificationEnabled)
        ? (state.endpointUrl || "").trim()
        : "",
    appActivationType: state.appActivationType || AppSubscriptions.AUTOMATIC,
    redirectURL:
        state.provisioningType === ProvisioningConstants.type.REDIRECT_URL
            ? (state.redirectURL || "").trim()
            : "",
});

const isEmailValid = (value) => {
    const email = (value || "").trim();
    if (!email) {
        return true;
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const SUBMIT_SOLUTION_ALLOWED_TOP_LEVEL_FIELDS = [
    "id",
    "listingVersion",
    "apigee_id",
    "developerId",
    "developerName",
    "activateOnly",
    "developerAgreement",
    "orgId",
    "status",
    "submittedOn",
    "lastUpdatedBy",
    "lastUpdatedOn",
    "solutionType",
    "overview",
    "highlights",
    "support",
    "features",
    "plans",
    "comments",
    "registration_details",
    "solution_visibility",
    "app_subscriptions",
    "provisioning_interface",
    "subscription_notifications",
    "partner_program_registration_details",
    "registeredOn",
    "listingVersionUpdates",
    "termsOfUse",
    "worksWithIds",
    "subscription_management",
    "data_rights",
    "oneToOneUI",
    "appPublishingFee",
    "listingFeeHistory",
    "dnpAppName",
    "certificationStatus",
    "appSettingsChangeHistory",
    "isAppSettingsChanged",
];

const filterSubmitSolutionTopLevelFields = (command) =>
    SUBMIT_SOLUTION_ALLOWED_TOP_LEVEL_FIELDS.reduce((acc, key) => {
        if (Object.prototype.hasOwnProperty.call(command, key)) {
            acc[key] = command[key];
        }
        return acc;
    }, {});

const generateRandomId = () => Math.random().toString(36).substr(2, 9);

const AppSettings = ({
    solutions,
    onOperationComplete,
    submitSolution,
    entity,
    userData,
    setSaveToast = () => { },
    changeMenu = () => { }
}) => {
    const latestSolution = useMemo(() => getLatestSolution(solutions), [
        solutions,
    ]);
    const latestSolutionSettings = useMemo(
        () => getSolutionSettingsSource(latestSolution),
        [latestSolution],
    );

    // Extract notification domains from entity
    const notificationDomains = useMemo(() => {
        try {
            if (entity && entity.notificationDomains) {
                const domains = typeof entity.notificationDomains === 'string'
                    ? JSON.parse(entity.notificationDomains)
                    : entity.notificationDomains;
                return Array.isArray(domains) ? domains : [];
            }
        } catch (error) {
            console.error("Failed to parse notification domains:", error);
        }
        return [];
    }, [entity]);

    const [formState, setFormState] = useState({
        technicalSupportEmail: "",
        subscriptionNotificationEmails: Array(SUBSCRIPTION_MAX_INPUT_COUNT).fill(
            "",
        ),
        provisioningType: ProvisioningConstants.type.MANUAL,
        appActivationType: AppSubscriptions.AUTOMATIC,
        apiNotificationEnabled: false,
        whitelistUrl: "",
        endpointUrl: "",
        redirectURL: "",
    });
    const [
        showAllSubscriptionEmailInputs,
        setShowAllSubscriptionEmailInputs,
    ] = useState(false);
    const [apiNotificationActiveTabIndex, setApiNotificationActiveTabIndex] = useState(0);
    const [apiResponseData, setApiResponseData] = useState({});
    const [isApiTestRunning, setIsApiTestRunning] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedNotificationDomain, setSelectedNotificationDomain] = useState("");
    const [endpointURLError, setEndpointURLError] = useState(false);
    const [isDomainDropdownOpen, setIsDomainDropdownOpen] = useState(false);
    const [isChangeHistoryDialogOpen, setIsChangeHistoryDialogOpen] = useState(false);
    const [changeHistoryTableData, setChangeHistoryTableData] = useState([]);
    const domainTargetRef = useRef();
    const domainDimensionRef = useRef();
    const domainMenuContainerRef = useRef();
    const [initialFormState, setInitialFormState] = useState(null);
    const [initialProvisioningRedirectType, setInitialProvisioningRedirectType] =
        useState("entityID");
    const [emailErrors, setEmailErrors] = useState({
        technicalSupportEmail: false,
        subscriptionNotificationEmails: Array(SUBSCRIPTION_MAX_INPUT_COUNT).fill(
            false,
        ),
    });
    const [provisioningRedirectType, setProvisioningRedirectType] = useState(
        "entityID",
    );
    const [redirectURLError, setRedirectURLError] = useState(false);
    const [appSubscribers, setAppSubscribers] = useState([]);
    const [hasAppSubscriptions, setHasAppSubscriptions] = useState(false);
    const [isLoadingAppSubscriptions, setIsLoadingAppSubscriptions] = useState(true);
    const [isSaveConfirmDialogOpen, setIsSaveConfirmDialogOpen] = useState(false);

    const tableContentRef = usePreventTableScrollingRef();
    const frameworkComponentsExtended = {
        textRenderer: TextRenderer,
    };

    useEffect(() => {
        const solutionId = latestSolution?.id;
        if (!solutionId) {
            setIsLoadingAppSubscriptions(false);
            return;
        }

        let isCancelled = false;

        const fetchSolutionSubscriptions = async () => {
            setIsLoadingAppSubscriptions(true);
            try {
                const client = getApolloClient();
                const response = await client.query({
                    query: GET_METADATA_SUBSCRIPTIONS_QUERY,
                    variables: {
                        solutionId,
                        randomId: generateRandomId(),
                    },
                });
                const subscriptions = response?.data?.getMetadataSubscriptions || [];

                const pendingSubscriptions = subscriptions.filter(
                    (subscription) =>
                        subscription?.appSubscriptionActivationState ===
                        PENDING_ACTIVATION_STATE,
                );
                if (!isCancelled) {
                    setAppSubscribers(subscriptions);
                    setHasAppSubscriptions(pendingSubscriptions.length > 0);
                }
            } catch (error) {
                if (!isCancelled) {
                    console.error("Failed to fetch app subscriptions", error);
                    setAppSubscribers([]);
                    setHasAppSubscriptions(false);
                }
            } finally {
                if (!isCancelled) {
                    setIsLoadingAppSubscriptions(false);
                }
            }
        };

        fetchSolutionSubscriptions();

        return () => {
            isCancelled = true;
        };
    }, [latestSolution?.id]);

    useEffect(() => {
        if (!latestSolutionSettings) {
            return;
        }

        const supportConfig = latestSolutionSettings.support || {};
        const overviewConfig = latestSolutionSettings.overview || {};
        const subscriptionConfigRaw =
            latestSolutionSettings.subscription_notifications || {};
        const subscriptionConfig =
            subscriptionConfigRaw.subscription_notifications || subscriptionConfigRaw;
        const appSubscriptionsConfig = latestSolutionSettings.app_subscriptions || {};
        const provisioningConfigRaw =
            latestSolutionSettings.provisioning_interface || {};
        const provisioningConfig =
            provisioningConfigRaw.provisioning_interface || provisioningConfigRaw;

        const technicalSupportEmail =
            latestSolutionSettings.technicalSupportEmail ||
            overviewConfig.outageNotificationsEmail ||
            supportConfig.emailAddress ||
            (supportConfig.support && supportConfig.support.emailAddress) ||
            "";

        const subscriptionNotificationEmails = mapEmailsToFixedCount(
            subscriptionConfig.email,
            SUBSCRIPTION_MAX_INPUT_COUNT,
        );

        const provisioningType =
            provisioningConfig.provisioningtype || ProvisioningConstants.type.MANUAL;

        const apiNotificationEnabled = Boolean(subscriptionConfig.apiNotification);

        const appActivationType =
            appSubscriptionsConfig.appSubscriptionsType || AppSubscriptions.AUTOMATIC;

        const endpointUrl = subscriptionConfig.endpointUrl || "";

        const redirectURL = provisioningConfig.redirectURL || "";

        // Determine redirect type (entityID or subscriptionID)
        let redirectType = "entityID";
        if (provisioningConfig.subscriptionID === true) {
            redirectType = "subscriptionID";
        } else if (provisioningConfig.entityID === true) {
            redirectType = "entityID";
        }

        setSelectedNotificationDomain("");

        const populatedFormState = {
            technicalSupportEmail,
            subscriptionNotificationEmails,
            provisioningType,
            appActivationType,
            apiNotificationEnabled,
            whitelistUrl: endpointUrl,
            endpointUrl: endpointUrl,
            redirectURL: redirectURL,
        };

        setFormState(populatedFormState);
        setInitialFormState(populatedFormState);
        setProvisioningRedirectType(redirectType);
        setInitialProvisioningRedirectType(redirectType);
        setEmailErrors({
            technicalSupportEmail: false,
            subscriptionNotificationEmails: Array(SUBSCRIPTION_MAX_INPUT_COUNT).fill(
                false,
            ),
        });

        const hasAdditionalEmailValues =
            subscriptionNotificationEmails[3] || subscriptionNotificationEmails[4];
        setShowAllSubscriptionEmailInputs(Boolean(hasAdditionalEmailValues));
    }, [latestSolutionSettings]);

    const hasUnsavedChanges = useMemo(() => {
        if (!initialFormState) {
            return false;
        }

        const formStateChanged =
            JSON.stringify(getComparableFormState(formState)) !==
            JSON.stringify(getComparableFormState(initialFormState));

        const redirectTypeChanged =
            provisioningRedirectType !== initialProvisioningRedirectType;

        return formStateChanged || redirectTypeChanged;
    }, [formState, initialFormState, provisioningRedirectType, initialProvisioningRedirectType]);

    const hasAnyEmailError = useMemo(
        () =>
            emailErrors.technicalSupportEmail ||
            emailErrors.subscriptionNotificationEmails.some(Boolean),
        [emailErrors],
    );

    const hasProvisioningURLError = useMemo(
        () => {
            if (formState.provisioningType !== ProvisioningConstants.type.REDIRECT_URL) {
                return false;
            }
            return !validateUrl(formState.redirectURL);
        },
        [formState.provisioningType, formState.redirectURL],
    );

    const hasApiNotificationError = useMemo(() => {
        if (!formState.apiNotificationEnabled) {
            return false;
        }
        return (
            !selectedNotificationDomain ||
            !formState.endpointUrl ||
            !validateUrl(formState.endpointUrl)
        );
    }, [
        formState.apiNotificationEnabled,
        formState.endpointUrl,
        selectedNotificationDomain,
    ]);

    const whitelistOptions = useMemo(() => {
        return (notificationDomains || []).filter((url, index, arr) => url && arr.indexOf(url) === index);
    }, [notificationDomains]);

    const allChangeHistoryEntries = useMemo(() => {
        const rawHistory = Array.isArray(
            latestSolutionSettings?.appSettingsChangeHistory,
        )
            ? latestSolutionSettings.appSettingsChangeHistory
            : [];

        return [...rawHistory].sort(
            (a, b) => new Date(b.changedOn) - new Date(a.changedOn),
        );
    }, [latestSolutionSettings]);

    const changeHistoryEntries = useMemo(
        () => allChangeHistoryEntries.slice(0, 5),
        [allChangeHistoryEntries],
    );

    const onGridReady = () => {
        setChangeHistoryTableData(changeHistoryEntries);
    };

    useEffect(() => {
        setChangeHistoryTableData(changeHistoryEntries);
    }, [changeHistoryEntries]);



    const handleOpenChangeHistoryDialog = () => setIsChangeHistoryDialogOpen(true);
    const handleCloseChangeHistoryDialog = () => setIsChangeHistoryDialogOpen(false);

    if (!latestSolution) {
        return <div>Unable to load App Settings.</div>;
    }

    const handleTechnicalSupportEmailChange = (event) => {
        const updatedValue = event?.target?.value || "";
        const hasError = !isEmailValid(updatedValue);
        setFormState((prevState) => ({
            ...prevState,
            technicalSupportEmail: updatedValue,
        }));
        setEmailErrors((prevState) => ({
            ...prevState,
            technicalSupportEmail: hasError,
        }));
    };

    const isRestrictedActivationTypeChange = (current, initial) =>
        hasAppSubscriptions &&
        initial.appActivationType === AppSubscriptions.MANUAL &&
        current.appActivationType === AppSubscriptions.AUTOMATIC;

    const handleSave = () => {
        if (
            !latestSolution ||
            !latestSolutionSettings ||
            !hasUnsavedChanges ||
            isSaving ||
            isLoadingAppSubscriptions ||
            hasAnyEmailError ||
            hasApiNotificationError
        ) {
            return;
        }

        if (hasProvisioningURLError) {
            setRedirectURLError(true);
            return;
        }
        window.scrollTo({ top: 0, behavior: "smooth" });


        const current = getComparableFormState(formState);
        const initial = getComparableFormState(initialFormState || {});

        if (isRestrictedActivationTypeChange(current, initial)) {
            setIsSaveConfirmDialogOpen(true);
            return;
        }

        performSave();
    };

    const handleCancelSaveConfirmDialog = () => {
        setIsSaveConfirmDialogOpen(false);
    };

    const handleResolveAppActivations = () => {
        setIsSaveConfirmDialogOpen(false);
        changeMenu("appActivation");
    };

    const performSave = async () => {
        setIsSaveConfirmDialogOpen(false);
        setSaveToast(null);
        setIsSaving(true);
        try {
            const current = getComparableFormState(formState);
            const initial = getComparableFormState(initialFormState || {});

            const command = JSON.parse(JSON.stringify(latestSolution));

            const hasTechnicalSupportEmailChanged =
                current.technicalSupportEmail !== initial.technicalSupportEmail;
            const hasProvisioningTypeChanged =
                current.provisioningType !== initial.provisioningType;
            const hasAppActivationTypeChanged =
                !isRestrictedActivationTypeChange(current, initial) &&
                current.appActivationType !== initial.appActivationType;

            const hasSubscriptionEmailsChanged =
                JSON.stringify(current.subscriptionNotificationEmails) !==
                JSON.stringify(initial.subscriptionNotificationEmails);
            const hasApiNotificationChanged =
                current.apiNotificationEnabled !== initial.apiNotificationEnabled;
            const hasEndpointUrlChanged =
                current.endpointUrl !== initial.endpointUrl;
            const hasRedirectURLChanged =
                current.redirectURL !== initial.redirectURL;
            const hasRedirectTypeChanged =
                provisioningRedirectType !== initialProvisioningRedirectType;

            const hasChangedSettingsFields =
                hasTechnicalSupportEmailChanged ||
                hasAppActivationTypeChanged ||
                hasProvisioningTypeChanged ||
                hasSubscriptionEmailsChanged ||
                hasApiNotificationChanged ||
                hasEndpointUrlChanged ||
                hasRedirectURLChanged ||
                hasRedirectTypeChanged;

            if (!hasChangedSettingsFields) {
                setIsSaving(false);
                return;
            }

            const changedByName =
                (userData && (userData.name || userData.email)) || "Unknown";
            const changedOn = new Date().toISOString();
            const dateTimeLabel = moment(changedOn).format(
                CHANGE_HISTORY_DATE_FORMAT,
            );
            const newChangeHistoryEntries = [];
            const addChangeHistoryEntry = (fieldName, oldValue, newValue) => {
                newChangeHistoryEntries.push({
                    fieldName,
                    oldValue: oldValue || "-",
                    newValue: newValue || "-",
                    changedBy: changedByName,
                    dateTime: dateTimeLabel,
                    changedOn,
                });
            };

            if (hasTechnicalSupportEmailChanged) {
                addChangeHistoryEntry(
                    "Technical Support Email",
                    initial.technicalSupportEmail,
                    current.technicalSupportEmail,
                );
            }

            if (hasProvisioningTypeChanged) {
                addChangeHistoryEntry(
                    "Provisioning Options",
                    getProvisioningTypeLabel(initial.provisioningType),
                    getProvisioningTypeLabel(current.provisioningType),
                );
            }

            if (hasAppActivationTypeChanged) {
                addChangeHistoryEntry(
                    "App Activation",
                    initial.appActivationType,
                    current.appActivationType,
                );
            }

            if (hasRedirectURLChanged) {
                addChangeHistoryEntry(
                    "Provisioning Redirect URL",
                    initial.redirectURL,
                    current.redirectURL,
                );
            }

            if (hasRedirectTypeChanged) {
                addChangeHistoryEntry(
                    "Provisioning Redirect Type",
                    getRedirectTypeLabel(initialProvisioningRedirectType),
                    getRedirectTypeLabel(provisioningRedirectType),
                );
            }

            if (hasSubscriptionEmailsChanged) {
                addChangeHistoryEntry(
                    "Subscription Notification Emails",
                    initial.subscriptionNotificationEmails.filter(Boolean).join(", "),
                    current.subscriptionNotificationEmails.filter(Boolean).join(", "),
                );
            }

            if (hasApiNotificationChanged) {
                addChangeHistoryEntry(
                    "API Notification",
                    initial.apiNotificationEnabled ? "Active" : "Inactive",
                    current.apiNotificationEnabled ? "Active" : "Inactive",
                );
            }

            if (hasEndpointUrlChanged) {
                addChangeHistoryEntry(
                    "API Notification URL",
                    initial.endpointUrl,
                    current.endpointUrl,
                );
            }

            const existingChangeHistory = Array.isArray(
                latestSolutionSettings?.appSettingsChangeHistory,
            )
                ? latestSolutionSettings.appSettingsChangeHistory
                : [];
            command.appSettingsChangeHistory = [
                ...newChangeHistoryEntries,
                ...existingChangeHistory,
            ];

            if (hasTechnicalSupportEmailChanged) {
                command.overview = command.overview || {};
                command.overview.outageNotificationsEmail =
                    current.technicalSupportEmail;
            }

            if (hasProvisioningTypeChanged || hasRedirectURLChanged || hasRedirectTypeChanged) {
                command.provisioning_interface = command.provisioning_interface || {};
                command.provisioning_interface.provisioningtype =
                    current.provisioningType;

                if (current.provisioningType === ProvisioningConstants.type.REDIRECT_URL) {
                    command.provisioning_interface.redirectURL = current.redirectURL;
                    command.provisioning_interface.entityID =
                        provisioningRedirectType === "entityID";
                    command.provisioning_interface.subscriptionID =
                        provisioningRedirectType === "subscriptionID";
                } else {
                    command.provisioning_interface.redirectURL = "";
                    command.provisioning_interface.entityID = false;
                    command.provisioning_interface.subscriptionID = false;
                }
            }

            if (
                hasSubscriptionEmailsChanged ||
                hasApiNotificationChanged ||
                hasEndpointUrlChanged
            ) {
                command.subscription_notifications =
                    command.subscription_notifications || {};

                if (hasSubscriptionEmailsChanged) {
                    command.subscription_notifications.email =
                        current.subscriptionNotificationEmails
                            .filter(Boolean)
                            .join(",");
                }

                if (hasApiNotificationChanged) {
                    command.subscription_notifications.apiNotification =
                        current.apiNotificationEnabled;
                }

                if (hasEndpointUrlChanged) {
                    command.subscription_notifications.endpointUrl =
                        current.apiNotificationEnabled ? current.endpointUrl : "";
                }
            }

            if (hasAppActivationTypeChanged) {
                command.app_subscriptions = command.app_subscriptions || {};
                command.app_subscriptions.appSubscriptionsType = current.appActivationType;
            }

            if (!submitSolution || typeof submitSolution.execute !== "function") {
                throw new Error("submitSolution command is not available");
            }
            command.isAppSettingsChanged = true;
            const filteredCommand = filterSubmitSolutionTopLevelFields(command);
            await submitSolution.execute(filteredCommand);

            setInitialFormState(formState);
            setSaveToast({
                variant: TOAST_VARIANTS.POSITIVE,
                content:
                    "App settings saved successfully. Your app listing status remains approved and no resubmission is required.",
            });
            if (onOperationComplete && typeof onOperationComplete === "function") {
                await onOperationComplete();
            }
        } catch (error) {
            console.error("Failed to save app settings", error);
            setSaveToast({
                variant: TOAST_VARIANTS.NEGATIVE,
                content: "Unable to save app settings. Please try again.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmSaveDialog = () => {
        performSave();
    };

    const handleSubscriptionNotificationEmailChange = (index, value) => {
        const hasError = !isEmailValid(value);
        setFormState((prevState) => {
            const updatedEmails = [...prevState.subscriptionNotificationEmails];
            updatedEmails[index] = value;
            return {
                ...prevState,
                subscriptionNotificationEmails: updatedEmails,
            };
        });
        setEmailErrors((prevState) => {
            const nextErrors = [...prevState.subscriptionNotificationEmails];
            nextErrors[index] = hasError;
            return {
                ...prevState,
                subscriptionNotificationEmails: nextErrors,
            };
        });
    };

    const visibleSubscriptionInputCount = showAllSubscriptionEmailInputs
        ? SUBSCRIPTION_MAX_INPUT_COUNT
        : SUBSCRIPTION_VISIBLE_INPUT_COUNT;

    const shouldShowAddMoreButton = !showAllSubscriptionEmailInputs;

    const handleProvisioningTypeChange = (updatedValue) => {
        setFormState((prevState) => ({
            ...prevState,
            provisioningType: updatedValue || ProvisioningConstants.type.MANUAL,
        }));
        setRedirectURLError(false);
    };

    const handleAppActivationTypeChange = (updatedValue) => {
        setFormState((prevState) => ({
            ...prevState,
            appActivationType: updatedValue || AppSubscriptions.AUTOMATIC,
        }));
    };

    const handleRedirectTypeChange = (value) => {
        setProvisioningRedirectType(value);
        setRedirectURLError(false);
    };

    const handleRedirectURLChange = (event) => {
        const value = event?.target?.value || "";
        setFormState((prevState) => ({
            ...prevState,
            redirectURL: value,
        }));
        setRedirectURLError(!validateUrl(value));
    };

    const handleApiNotificationToggle = (event) => {
        const checked = Boolean(event?.target?.checked);
        setFormState((prevState) => ({
            ...prevState,
            apiNotificationEnabled: checked,
            endpointUrl:
                checked && !prevState.endpointUrl
                    ? prevState.whitelistUrl
                    : prevState.endpointUrl,
        }));
        if (!checked) {
            setSelectedNotificationDomain("");
            setEndpointURLError(false);
        }
    };

    const handleNotificationDomainChange = (domain) => {
        setSelectedNotificationDomain(domain);
        setIsDomainDropdownOpen(false);
        setEndpointURLError(false);
    };

    const handleDomainDropdownOpen = () => {
        setIsDomainDropdownOpen(true);
    };

    const handleDomainDropdownClose = () => {
        setIsDomainDropdownOpen(false);
    };

    const handleEndpointURLChange = (event) => {
        const value = event?.target?.value || "";
        setFormState((prevState) => ({
            ...prevState,
            endpointUrl: value,
        }));
        setEndpointURLError(!validateUrl(value));
    };

    const handleWhitelistUrlChange = (event) => {
        const value = event?.target?.value || "";
        setFormState((prevState) => ({
            ...prevState,
            whitelistUrl: value,
            endpointUrl: prevState.endpointUrl || value,
        }));
    };

    const handleApiTest = async () => {
        if (!formState.endpointUrl || endpointURLError) {
            return;
        }

        setIsApiTestRunning(true);
        try {
            const client = getApolloClient();
            const response = await client.mutate({
                mutation: NOTIFY_URL_TEST_MUTATION,
                variables: {
                    command: {
                        endpointUrl: formState.endpointUrl,
                        payload: JSON.stringify(API_REQUEST_BODY),
                    },
                },
            });
            const notifyStatus = response?.data?.notifyUrl?.status;
            const graphQLErrors = response?.errors;
            const isSuccessful = notifyStatus === "Successful";

            setApiResponseData({
                status: notifyStatus || "FAILED",
                message: isSuccessful
                    ? "Test notification sent"
                    : graphQLErrors && graphQLErrors.length
                        ? graphQLErrors[0].message
                        : "Test notification failed",
                errors: graphQLErrors || [],
                response,
            });
            setApiNotificationActiveTabIndex(1);
        } catch (error) {
            const graphQLError =
                error?.graphQLErrors && error.graphQLErrors.length
                    ? error.graphQLErrors[0].message
                    : null;
            setApiResponseData({
                status: "FAILED",
                message: graphQLError || "Test notification failed",
                errors: error?.graphQLErrors || [],
                error: error?.message || error,
                response: null,
            });
            setApiNotificationActiveTabIndex(1);
        } finally {
            setIsApiTestRunning(false);
        }
    };



    return (
        <div className="app-settings-panel">
            <div className="app-settings-subtitle">
                Update these settings without requiring a new version of your app.
            </div>

            <div className="app-settings-section">
                <div className="app-settings-technical-support-email-section">
                    <div className="app-settings-technical-support-email-label">
                        1. Technical Support Email
                    </div>
                    <span className="app-settings-technical-support-helper-text">
                        Email address for technical outage notifications and critical
                        support issues. &nbsp;
                        <LearnMore url={`${environmentURLs.docs}docs/tutorials/app-lifecycle/app-submission-form/#the-settings-page`} />
                    </span>
                    <div className="app-settings-technical-support-input-box">
                        <Input
                            label={"Email Address"}
                            value={formState.technicalSupportEmail}
                            onChange={handleTechnicalSupportEmailChange}
                            enableCustomValidation
                            hasError={emailErrors.technicalSupportEmail}
                            errorMessage={EMAIL_VALIDATION_MESSAGE}

                        />
                    </div>
                </div>

                <div className="app-settings-subscription-notification-email-section">
                    <div className="app-settings-subscription-notification-email-label">
                        2. Subscription Notifications Email
                    </div>
                    <span className="app-settings-subscription-notification-helper-text">
                        We will send new subscription emails to the address(es) listed
                        below. You can add upto 5 email addresses. &nbsp;
                        <LearnMore url={`${environmentURLs.docs}docs/tutorials/app-lifecycle/app-submission-form/#the-settings-page`} />
                    </span>
                    <div className="app-settings-subscription-notification-input-boxes">
                        {formState.subscriptionNotificationEmails
                            .slice(0, visibleSubscriptionInputCount)
                            .map((email, index) => (
                                <div
                                    className="app-settings-email-input"
                                    key={`subscription-notification-email-${index}`}
                                >
                                    <Input
                                        label={`Email Address ${index + 1}`}
                                        value={email}
                                        onChange={(event) =>
                                            handleSubscriptionNotificationEmailChange(
                                                index,
                                                event?.target?.value || "",
                                            )
                                        }
                                        enableCustomValidation
                                        hasError={Boolean(
                                            emailErrors.subscriptionNotificationEmails[index],
                                        )}
                                        errorMessage={EMAIL_VALIDATION_MESSAGE}
                                    />
                                </div>
                            ))}
                        {shouldShowAddMoreButton && (
                            <div className="app-settings-add-more-button-wrap">
                                <StyledTextButton
                                    icon={<IconAdd />}
                                    text="Add More"
                                    onClick={() => setShowAllSubscriptionEmailInputs(true)}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="app-settings-app-activation-section">
                    <div className="app-settings-app-activation-label">
                        3. App Activation
                    </div>
                    <span className="app-settings-app-activation-helper-text">
                        Choose how the app will be enabled when a user subscribes. &nbsp;
                        <LearnMore url={`${environmentURLs.docs}docs/tutorials/app-lifecycle/manual-app-activation`} />
                    </span>
                    <div className="app-settings-app-activation-options">
                        <div
                            className="app-settings-app-activation-radio-group"
                            data-test-id="app-activation-radio-group"
                        >
                            {APP_ACTIVATION_OPTIONS.map((option) => (
                                <div
                                    className="app-settings-app-activation-option"
                                    key={option.id}
                                    onClick={() => handleAppActivationTypeChange(option.value)}
                                >
                                    <Radio
                                        id={option.id}
                                        name="app-activation-type"
                                        checked={formState.appActivationType === option.value}
                                        onChange={() => handleAppActivationTypeChange(option.value)}
                                        className="app-settings-app-activation-radio"
                                        size={RADIO_SIZES.LARGE}
                                    />
                                    <div className="app-settings-app-activation-option-content">
                                        <label
                                            htmlFor={option.id}
                                            className="app-settings-app-activation-option-label"
                                        >
                                            {option.label}
                                        </label>
                                        <div className="app-settings-app-activation-option-description">
                                            {option.description}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="app-settings-provisioning-options-section">
                    <div className="app-settings-provisioning-options-label">
                        4. Provisioning
                    </div>
                    <span className="app-settings-provisioning-options-helper-text">
                        By selecting an option, you will do the provisioning outside
                        Fortellis. &nbsp;
                        <LearnMore url={`${environmentURLs.docs}docs/tutorials/app-lifecycle/app-submission-form/#the-provisioning-options-page`} />
                    </span>
                    <div className="app-settings-provisioning-options">
                        <div
                            className="app-settings-provisioning-radio-group"
                            data-test-id="provisioning-radio-group"
                        >
                            {PROVISIONING_OPTIONS.map((option) => (
                                <div
                                    className="app-settings-provisioning-option"
                                    key={option.id}
                                >
                                    <Radio
                                        checked={formState.provisioningType === option.value}
                                        onChange={() => handleProvisioningTypeChange(option.value)}
                                        className="app-settings-provisioning-radio"
                                        size={RADIO_SIZES.LARGE}
                                    />
                                    <div className="app-settings-provisioning-option-content">
                                        <label
                                            className="app-settings-provisioning-option-label"
                                            onClick={() => handleProvisioningTypeChange(option.value)}
                                        >
                                            {option.label}
                                        </label>
                                        <div className="app-settings-provisioning-option-description">
                                            {option.description}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {formState.provisioningType === ProvisioningConstants.type.REDIRECT_URL && (
                            <>
                                <div className="app-settings-redirect-url-input">
                                    <Input
                                        label="Website URL"
                                        value={formState.redirectURL}
                                        onChange={handleRedirectURLChange}
                                        enableCustomValidation
                                        isRequired={true}
                                        hasError={redirectURLError}
                                        errorMessage={URL_VALIDATION_MESSAGE}
                                        placeholder="https://example.com"
                                    />
                                </div>

                                <div className="app-settings-provisioning-url-section">
                                    <div className="app-settings-provisioning-url-subheading">Select Provisioning Option</div>
                                    <div className="app-settings-provisioning-url-options">
                                        <div className="app-settings-provisioning-redirect-radio-group">
                                            {REDIRECT_TYPE_OPTIONS.map((option) => (
                                                <div
                                                    className="app-settings-provisioning-redirect-option"
                                                    key={option.id}
                                                >
                                                    <Radio
                                                        checked={provisioningRedirectType === option.value}
                                                        onChange={() => handleRedirectTypeChange(option.value)}
                                                        className="app-settings-provisioning-redirect-radio"
                                                        size={RADIO_SIZES.LARGE}
                                                    />
                                                    <div className="app-settings-provisioning-redirect-option-content">
                                                        <label
                                                            className="app-settings-provisioning-redirect-option-label"
                                                            onClick={() => handleRedirectTypeChange(option.value)}
                                                        >
                                                            {option.label}
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="overview-row-container-one-col">
                                        <Card data-testid="card" className='card card-purple provisioning'>
                                            <div className="card-content">
                                                <div className='card-heading'>Communicate Provisioning Status</div>
                                                <div className='card-subheading'>Fortellis UI for reporting provisioning status</div>
                                                <div className='card-text'>Developer can use UI control available in subscription details page to mark the provisioning status as complete.</div>
                                                <div className='card-subheading'>Fortellis endpoint for reporting provisioning status</div>
                                                <div className='card-text'>https://api.fortellis.io/provisioningStatus</div>
                                            </div>
                                        </Card>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="app-settings-api-notification-section">
                    <div className="app-settings-api-notification-label">
                        5. API Notification
                    </div>
                    <span className="app-settings-api-notification-helper-text">
                        By checking this item, we will notify the URL below about new
                        subscriptions.
                    </span>
                    <div className="app-settings-api-notification-content">
                        <div className="app-settings-api-checkbox-wrap">
                            <Checkbox
                                id="api-notifications"
                                label="Enable API notifications"
                                checked={formState.apiNotificationEnabled}
                                onChange={handleApiNotificationToggle}
                            />
                        </div>

                        {formState.apiNotificationEnabled && (
                            <>
                                <div className="app-settings-api-controls-grid">
                                    <div className="app-settings-api-domain-select">
                                        <div style={{ width: "auto" }} data-testid="domain-dropdown">
                                            <Input
                                                dataTestId="domain-menu-input"
                                                icon={isDomainDropdownOpen ? <IconArrowDropUp /> : <IconArrowDropDown />}
                                                id="app-settings-notification-domain"
                                                label="Select Notification Domain"
                                                labelRef={domainDimensionRef}
                                                name="domain-dropdown"
                                                onChange={() => { }}
                                                placeholder="Select"
                                                ref={domainTargetRef}
                                                value={selectedNotificationDomain}
                                                style={{ caretColor: "transparent" }}
                                                className="app-settings-domain-input"
                                            />
                                        </div>
                                        <Menu
                                            dataTestId="domain-menu-container"
                                            dimensionRef={domainDimensionRef}
                                            isOpen={isDomainDropdownOpen}
                                            labelRef={domainTargetRef}
                                            onClose={handleDomainDropdownClose}
                                            onOpen={handleDomainDropdownOpen}
                                            style={{ width: "auto", zIndex: 50 }}
                                            isAuto={false}
                                            ref={domainMenuContainerRef}
                                        >
                                            {whitelistOptions.map((option, ind) => (
                                                <MenuListItem
                                                    dataTestId={`domain-menu-item-${option}`}
                                                    isSelected={option === selectedNotificationDomain}
                                                    key={ind}
                                                    onClick={() => handleNotificationDomainChange(option)}
                                                    hideSelectedItemCheckmark={false}
                                                >
                                                    {option}
                                                </MenuListItem>
                                            ))}
                                        </Menu>
                                    </div>

                                    <div className="app-settings-api-url-input">
                                        <Input
                                            label="Endpoint URL"
                                            value={formState.endpointUrl}
                                            onChange={handleEndpointURLChange}
                                            enableCustomValidation
                                            isRequired
                                            hasError={endpointURLError}
                                            errorMessage={URL_VALIDATION_MESSAGE}
                                            isDisabled={!selectedNotificationDomain}
                                            placeholder="https://example.com/endpoint"
                                        />
                                    </div>

                                    <TextButton
                                        text="Add Whitelist URL"
                                        dataTestId="open-new-tab"
                                        icon={<div><IconOpenInNew /></div>}
                                        style={{ width: "fit-content", marginTop: "6px" }}
                                        onClick={() =>
                                            window.open(
                                                environmentURLs.orgManagement,
                                                "_blank",
                                                "noopener,noreferrer",
                                            )
                                        }

                                    />
                                </div>

                                <div>
                                    <Tabs
                                        activeItemIndex={apiNotificationActiveTabIndex}
                                        items={[
                                            {
                                                id: "tab-api-request",
                                                panelId: "panel-api-request",
                                                renderPanel: (props) => (
                                                    <TabPanel {...props}>
                                                        <div>
                                                            <JsonTextArea src={API_REQUEST_BODY} />
                                                        </div>
                                                    </TabPanel>
                                                ),
                                                renderTab: (props) => (
                                                    <Tab
                                                        label="Request"
                                                        className="app-settings-api-tab"
                                                        hideLabel={false}
                                                        {...props}
                                                    />
                                                ),
                                            },
                                            {
                                                id: "tab-api-response",
                                                panelId: "panel-api-response",
                                                renderPanel: (props) => (
                                                    <TabPanel {...props}>
                                                        <div>
                                                            <JsonTextArea src={apiResponseData || {}} />
                                                        </div>
                                                    </TabPanel>
                                                ),
                                                renderTab: (props) => (
                                                    <Tab
                                                        label="Response"
                                                        className="app-settings-api-tab"
                                                        hideLabel={false}
                                                        {...props}
                                                    />
                                                ),
                                            },
                                        ]}
                                        onActiveItemChange={setApiNotificationActiveTabIndex}
                                        tabsLabel="API Notification Tabs"
                                    />
                                </div>

                                <div className="app-settings-api-test-button-wrap">
                                    <Button
                                        text={isApiTestRunning ? "Testing..." : "Test"}
                                        variant="primary"
                                        onClick={handleApiTest}
                                        isDisabled={isApiTestRunning || !formState.endpointUrl || endpointURLError || !selectedNotificationDomain}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="app-settings-warning-notification-toast">
                    <StyledToast
                        content="Changes will be applied immediately without requiring approval"
                        id="toast-id"
                        variant="warning"
                    />
                </div>
                <div className="app-settings-change-history-section">
                    <div className="app-settings-change-history-label">
                        Change History
                    </div>
                    <span className="app-settings-change-history-helper-text">
                        The table shows the top 5 recent changes made to this page. To view all the changes,
                    </span>

                    <UnderlinedTextButton
                        text="click here"
                        onClick={handleOpenChangeHistoryDialog}
                    />

                    <TableContainer className="app-settings-change-history-table-container ag-theme-balham">
                        <TableContent data-testid="app-settings-change-history-table" ref={tableContentRef}>
                            <AgGridReact
                                columnDefs={CHANGE_HISTORY_COLUMN_DEFS}
                                rowData={changeHistoryTableData}
                                onGridReady={onGridReady}
                                frameworkComponents={frameworkComponentsExtended}
                                domLayout="autoHeight"
                                headerHeight={56}
                            />
                        </TableContent>
                    </TableContainer>
                </div>
            </div>
            <div className="app-settings-save-button-wrap">
                <PrimaryButton
                    text="Save Changes"
                    onClick={handleSave}
                    isDisabled={!hasUnsavedChanges || isSaving || isLoadingAppSubscriptions || hasAnyEmailError || hasProvisioningURLError || hasApiNotificationError}
                />
            </div>
            {(isSaving || isLoadingAppSubscriptions) && (
                <div className="app-settings-loader-overlay">
                    <Loader />
                </div>
            )}
            <StyledDialog
                isOpen={isChangeHistoryDialogOpen}
                onClose={handleCloseChangeHistoryDialog}
                title="Change History Log"
                hideCloseButton
                className="app-settings-change-history-dialog"
            >
                <div className="app-settings-change-history-dialog-helper-text">
                    All changes to app settings are logged here
                </div>
                <TableContainer className="app-settings-change-history-table-container ag-theme-balham">
                    <TableContent data-testid="app-settings-change-history-dialog-table">
                        <AgGridReact
                            columnDefs={CHANGE_HISTORY_COLUMN_DEFS}
                            rowData={allChangeHistoryEntries}
                            domLayout="autoHeight"
                            headerHeight={56}
                        />
                    </TableContent>
                </TableContainer>
                <div className="app-settings-change-history-dialog-close-button">
                    <TextButton
                        text="Close"
                        onClick={handleCloseChangeHistoryDialog}
                    />
                </div>
            </StyledDialog>
            <StyledSaveDialog
                isOpen={isSaveConfirmDialogOpen}
                onClose={handleCancelSaveConfirmDialog}
                title="Action Needed"
                buttonsProps={[
                    {
                        id: 'dialog-action-2',
                        text: 'Save Changes',
                        variant: BUTTON_VARIANTS.SECONDARY,
                        onClick: handleConfirmSaveDialog,
                    },
                    {
                        id: 'dialog-action-1',
                        text: 'Resolve App Activations',
                        variant: BUTTON_VARIANTS.PRIMARY,
                        onClick: handleResolveAppActivations,
                    },
                ]}
                className="app-settings-save-confirm-dialog"
            >
                <div className="app-settings-save-confirm-dialog-helper-text">
                    You have pending manual subscription requests that must be resolved. Please approve or deny outstanding subscription requests and try again. All other changes will be saved.
                </div>
            </StyledSaveDialog>
        </div>
    );
};

export default AppSettings;
